import { type FC, useCallback } from "react";
import { useNavigate } from "react-router-dom";

import { useActorSystemEnabled } from "../../hooks/useActorSystemEnabled";
import { useAllRepositories } from "../../hooks/useAllRepositories";
import { useDashboardAuth } from "../../hooks/useDashboardAuth";
import { usePendingInviteCount } from "../../hooks/usePendingInviteCount";
import { useRepositoryChatEnabled } from "../../hooks/useRepositoryChatEnabled";
import { HudNavItem } from "../common/HudNavItem";
import { NAV_ITEMS, type NavKey } from "./constants";
import { CreditBalanceIndicator } from "./CreditBalanceIndicator";
import { OrgSwitcher } from "./OrgSwitcher";
import { SidebarGroupHeader } from "./SidebarGroupHeader";
import { SidebarRepoGrid } from "./SidebarRepoGrid";

const SECTION_KEYS = new Set<NavKey>(
  NAV_ITEMS.filter((item) => "section" in item).map((item) => item.key),
);

interface SidebarProps {
  activeView: NavKey;
  onNavClick: (key: NavKey) => void;
}

export const Sidebar: FC<SidebarProps> = ({ activeView, onNavClick }) => {
  const { globalRole } = useDashboardAuth();
  const { count: pendingInviteCount } = usePendingInviteCount();
  const { repositories } = useAllRepositories();
  const actorsEnabled = useActorSystemEnabled().enabled;
  const repositoryChat = useRepositoryChatEnabled();
  const nav = useNavigate();

  const handleNavigateToIndexing = useCallback(
    (repoSlug: string) => {
      void nav(
        `/integrations?tab=indexing&action=index&repo=${encodeURIComponent(repoSlug)}`,
      );
    },
    [nav],
  );

  const visibleItems = getVisibleNavItems({
    actorsEnabled,
    globalRole,
    repositoryChatEnabled: repositoryChat.enabled,
  });

  const navItems = visibleItems.map((item) => ({
    ...item,
    active: item.key === activeView,
  }));

  const mainNavItems = navItems.filter((item) => !SECTION_KEYS.has(item.key));
  const sectionNavItems = navItems.filter((item) => SECTION_KEYS.has(item.key));

  const isChildActive = (parentKey: string) =>
    navItems.some(
      (item) => "parent" in item && item.parent === parentKey && item.active,
    );

  return (
    <>
      <div className="flex min-h-0 flex-1 flex-col space-y-4 overflow-hidden lg:space-y-6">
        <div className="flex-shrink-0">
          <div className="flex items-center gap-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.22em] text-slate-400 dark:text-slate-500">
            <span aria-hidden className="text-slate-300 dark:text-slate-700">
              [
            </span>
            <span>Arti :: Workspace</span>
            <span aria-hidden className="text-slate-300 dark:text-slate-700">
              ]
            </span>
          </div>
          <p className="mt-1 font-mono text-sm font-bold uppercase tracking-widest text-slate-900 dark:text-slate-100">
            Command Center
          </p>
        </div>

        <nav className="flex-1 space-y-1 overflow-y-auto pr-1">
          {mainNavItems.map((item) => {
            if ("groupHeader" in item && item.groupHeader) {
              return (
                <SidebarGroupHeader
                  key={item.label}
                  label={item.label}
                  descriptor={item.descriptor}
                  isActive={isChildActive(item.key)}
                />
              );
            }
            return (
              <HudNavItem
                key={item.label}
                label={item.label}
                descriptor={item.descriptor}
                isActive={item.active}
                isChild={"parent" in item}
                showAlertDot={item.key === "settings" && pendingInviteCount > 0}
                onClick={() => onNavClick(item.key)}
              />
            );
          })}

          {sectionNavItems.length > 0 && (
            <div className="mt-4 pt-3">
              <div className="mb-2 flex items-center gap-2 px-3">
                <span
                  aria-hidden
                  className="text-slate-300 dark:text-slate-700"
                >
                  [
                </span>
                <span className="font-mono text-[9px] font-bold uppercase tracking-[0.22em] text-slate-400 dark:text-slate-500">
                  Administration
                </span>
                <span
                  aria-hidden
                  className="text-slate-300 dark:text-slate-700"
                >
                  ]
                </span>
                <span className="h-px flex-1 bg-slate-200 dark:bg-slate-800" />
              </div>
              {sectionNavItems.map((item) => (
                <HudNavItem
                  key={item.label}
                  label={item.label}
                  descriptor={item.descriptor}
                  isActive={item.active}
                  isChild={false}
                  showAlertDot={
                    item.key === "settings" && pendingInviteCount > 0
                  }
                  onClick={() => onNavClick(item.key)}
                />
              ))}
            </div>
          )}
        </nav>
      </div>

      {repositories.length > 0 && (
        <div className="flex-shrink-0 pb-1.5 pt-3">
          <SidebarRepoGrid
            repositories={repositories}
            onNavigateToIndexing={handleNavigateToIndexing}
          />
        </div>
      )}

      <div className="flex-shrink-0 border-t border-slate-200 pt-3 dark:border-slate-800">
        <CreditBalanceIndicator />
      </div>

      <div className="flex-shrink-0 border-t border-slate-200 pt-3 dark:border-slate-800">
        <OrgSwitcher />
      </div>
    </>
  );
};

export function getVisibleNavItems(input: {
  actorsEnabled: boolean;
  globalRole: string | null;
  repositoryChatEnabled: boolean;
}) {
  return NAV_ITEMS.filter((item) => {
    if (item.key === "admin" && input.globalRole !== "admin") {
      return false;
    }
    if (
      (item.key === "agents" ||
        item.key === "agents-instances" ||
        item.key === "agents-templates") &&
      !input.actorsEnabled
    ) {
      return false;
    }
    if (
      "featureFlag" in item &&
      item.featureFlag === "repository_chat" &&
      !input.repositoryChatEnabled
    ) {
      return false;
    }
    return true;
  });
}
